import * as SearchBoardActions from '@store/search-board/search-board.actions';
import {
  SearchBoardState,
  ExportTemplate,
  GridItemSize,
  GridViewState,
  INDICATOR_CONSTS,
  MAX_SEARCHES_RETAINED, PointOfInterestPoints,
  PropertyInformation,
  RecentSearches,
  SelectedRow,
  Shape,
  SidePanelTab
} from '@store/search-board/search-board.model';
import { MapTypes, OverlayType } from '@shared/models/googlemaps.model';
import { LayerInfo } from '@root/search/models/map.models';
import {
  BOUNDARY_LAYERS,
  DISTRESSED_PROPERTY_LAYERS,
  LayerId,
  LOCATION_INFORMATION_LAYERS,
  MLS_LISTINGS_LAYERS,
  PointOfInterestPreferenceValues,
  PointOfInterestProperty, PREMIUM_MAP_LAYER_NONE,
  PREMIUM_SEARCH_LAYERS,
  PROPERTY_CHARACTERISTIC_NONE,
  PROPERTY_CHARACTERISTICS_LAYERS,
  SALES_VALUES_LAYERS,
  TREND_LAYERS,
  TRENDS_LAYER_DEFAULTS,
  TRENDS_TYPE_NONE,
  TrendsAggregationLevel,
  TrendsPropertyType
} from '@root/search/models/map.constants';
import {
  DataElement,
  DisplayOptions,
  PreferenceCode,
  PreferenceElement,
  PreferenceGroup,
  Template
} from '@store/user/user.model';
import * as UserActions from '@store/user/user.actions';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { FieldCode } from '@root/search/models/display-fields.constant';
import { PropertyUtil } from '@shared/utils/property-util';

export type Action = SearchBoardActions.All;

export const searchBoardInitialState: SearchBoardState = {
  selectedRowId: {
    current: '',
    previous: ''
  },
  checkedProperties: [],
  sidePanelView: {
    sidePanelOpened: true,
    sidePanelTabOpened: SidePanelTab.quickSearch
  },
  gridViewState: {
    map: GridItemSize.HALF,
    list: GridItemSize.HALF
  },
  gridPage: 0,
  selectedResultsView: true,
  searchResult: {
    totalRecords: 0,
    propertySummaryList: []
  },
  mySearchFieldsData: [],
  quickSearchFieldsData: [],
  searchResultsCount: 0,
  currentPageName: 'Search Results',
  mapLayers: {
    mapZoom: 12,
    mapTypeId: MapTypes.ROADMAP,
    selectedDrawingTools: null,
    shapes: [],
    lines: [],
    annotationToolStatus: false,
    locateOnMapStatus: false,
    annotations: [],
    pointOfInterest: {
      config: [],
      selectedProperty: [],
      points: null
    },
    selectedMapLayer: PREMIUM_MAP_LAYER_NONE
  },
  boundaries: [],
  selectedMySearch: null,
  selectedMySearchModal: null,
  propertySales: {
    mlsListings: [],
    salesValues: [],
    distressedProperties: [],
    locationInformation: [],
    propertyCharacteristics: [],
    selectedPropertyCharacteristic: PROPERTY_CHARACTERISTIC_NONE,
  },
  propertyInformation: null,
  trends: TRENDS_LAYER_DEFAULTS,
  trendsLayerMarker: {
    lat: 0,
    lng: 0,
    layerId: LayerId.SELECT_NONE,
    isOpen: false,
    data: null
  },
  floodZoneMarker: {
    lat: 0,
    lng: 0,
    layerId: LayerId.SELECT_NONE,
    isOpen: false,
    data: null
  },
  mapLegendData: null,
  lookups: null,
  searchMultiFieldLookUps: [],
  submitFormTrigger: false,
  mapCoordinates: null,
  mapMessage: null,
  searchResultsSortingPreference: {
    sortField: '',
    sortOrder: '',
    allowedStatuses: [
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_SUBJECT_PROPERTY,
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ACTIVE,
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_PENDING,
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_EXPIRED,
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_SOLD,
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ESCROW_SHOW,
      INDICATOR_CONSTS.MLS_STD_MLS_STATUS_ESCROW_NOT_SHOW
    ],
    showOnlyWithPhoto: false
  },
  driveDirection: {
    from: '',
    to: ''
  },
  recentSearches: {
    [FieldCode.APN]: [],
    [FieldCode.SITE_ADDRESS]: [],
    [FieldCode.OWNER_NAME_1]: [],
    [FieldCode.MLS_LISTING_NUMBER]: [],
  },
  disclaimer: null
};

export function SearchBoardReducer(
  state: SearchBoardState = searchBoardInitialState,
  action: SearchBoardActions.All | UserActions.All): SearchBoardState {
  let selectedPropertyCharacteristic: LayerInfo = PROPERTY_CHARACTERISTIC_NONE;
  let selectedPremiumMap: LayerInfo = PREMIUM_MAP_LAYER_NONE;
  switch (action.type) {

    case SearchBoardActions.CHANGE_GRID_DATA:
      const {map, list} = action.payload;

      if (map === GridItemSize.ZERO && list === GridItemSize.FULL) {
        return {
          ...state,
          gridViewState: action.payload,
          selectedResultsView: false
        };
      }
      return {...state, gridViewState: action.payload};

    case SearchBoardActions.CHANGE_SIDE_PANEL_VIEW:
      return {
        ...state,
        sidePanelView: {
          ...state.sidePanelView,
          sidePanelOpened: action.payload
        }
      };

    case SearchBoardActions.CHANGE_SIDE_PANEL_TAB_VIEW:
      return {
        ...state,
        sidePanelView: {
          ...state.sidePanelView,
          sidePanelTabOpened: action.payload
        }
      };

    case SearchBoardActions.CHANGE_RESULTS_VIEW:
      const selected = !state.selectedResultsView;
      return {
        ...state,
        selectedResultsView: selected
      };
    case SearchBoardActions.SAVED_PREMIUM_LAYER_LOOKUPS:
      const saved = action.payload;
      return {
        ...state,
        savedPremiumLayerLookups: saved
      };
    case SearchBoardActions.CLEAR_ALL_PREMIUM:
      const clear = action.payload;
      return {
        ...state,
        clearAllPremium: clear
      };
    case SearchBoardActions.SET_RESULTS_VIEW_TO_CARDS:
      return {
        ...state,
        selectedResultsView: false
      };

    case SearchBoardActions.SELECT_ALL_PROPERTY:
      return {
        ...state,
        checkedProperties: action.payload ?
          state.searchResult.propertySummaryList.map((property: PropertyInformation) =>
            PropertyUtil.getIdFromPropertyInformation(property)) :
          []
      };

    case SearchBoardActions.SELECT_ALL_PROPERTY_MOBILE:
      return {
        ...state,
        checkedProperties: action.payload
      };

    case SearchBoardActions.CHANGE_PROPERTY_SELECTION:
      return {
        ...state,
        checkedProperties: action.payload
      };

    case SearchBoardActions.TOGGLE_PROPERTY_SELECTION:
      const {propertyId, isChecked} = action.payload;
      return {
        ...state,
        checkedProperties: getNewCheckedProperties(state.checkedProperties, propertyId, isChecked)
      };

    case SearchBoardActions.SELECT_ROW:
      return {
        ...state,
        selectedRowId: selectRow(state, action)
      };

    case SearchBoardActions.SET_SHOW_SELECTED:
      return {
        ...state,
        showSelected: action.payload
      };

    case SearchBoardActions.SET_CURRENT_PAGE_NAME:
      return {
        ...state,
        currentPageName: action.payload
      };

    case SearchBoardActions.CHANGE_DRAWING_TOOL:
      const data = action.payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          selectedDrawingTools: data === state.mapLayers.selectedDrawingTools ? null : data
        },
      };

    case SearchBoardActions.ADD_DRAWING_LAYER:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: [...state.mapLayers.shapes, action.payload]
        }
      };

    case SearchBoardActions.ADD_DRAWING_LINE:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          lines: [...state.mapLayers.lines, action.payload]
        }
      };

    case SearchBoardActions.REMOVE_DRAWING_LINE:
      const lineId = action.payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          lines: state.mapLayers.lines.filter((sh: Shape) => sh.id !== lineId)
        }
      };

    case SearchBoardActions.REMOVE_DRAWING_LAYER:
      const id = action.payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: state.mapLayers.shapes.filter((sh: Shape) => sh.id !== id)
        }
      };

    case SearchBoardActions.CHANGE_MAP_TYPE_ID:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          mapTypeId: action.payload
        }
      };

    case SearchBoardActions.SAVE_VALUES_OF_QUICK_SEARCH:
      return {
        ...state,
        quickSearchFieldsData: action.payload
      };

    case SearchBoardActions.SAVE_VALUES_OF_MY_SEARCH:
      return {
        ...state,
        mySearchFieldsData: action.payload,
        submitFormTrigger: false
      };


    case SearchBoardActions.DOWNLOAD_DISCLAIMER_SUCCESS:
      return {
        ...state,
        disclaimer: { ...state.disclaimer, [action.payload.latLong]: action.payload.disclaimer }
      };

    case SearchBoardActions.QUICK_SEARCH:
      return {
        ...state,
        recentSearches: action.payload.searchFields.reduce<RecentSearches>((recentSearches, searchField) => {
          if (recentSearches[searchField.fieldCode] && recentSearches[searchField.fieldCode].length < MAX_SEARCHES_RETAINED) {
            const values: Array<string | number> = searchField.operatorValues
              .reduce<Array<string | number>>((operatorValues, operatorInfo) => {
                return operatorValues.concat(operatorInfo.values);
              }, [])
              .filter((value, index, operatorInfoValues) => {
                return !recentSearches[searchField.fieldCode].includes(value) && operatorInfoValues.indexOf(value) === index;
              });
            recentSearches[searchField.fieldCode] = [...recentSearches[searchField.fieldCode], ...values];
          }
          return recentSearches;
        }, {...state.recentSearches})
      };

    case SearchBoardActions.SET_RECENT_SEARCHES:
      return {
        ...state,
        recentSearches: action.payload
      };

    case SearchBoardActions.SEARCH_SUCCESS:
      const checkedProperties: Array<string> = [];
      const propertySummaryList = PropertyUtil.getPropertySummaryList(action.payload)
        .map((propertyInformation: PropertyInformation) => {
          checkedProperties.push(PropertyUtil.getIdFromPropertyInformation(propertyInformation));
          return propertyInformation;
        });

      return {
        ...state,
        submitFormTrigger: false,
        searchResult: {
          totalRecords: propertySummaryList.length,
          propertySummaryList: propertySummaryList,
        },
        checkedProperties: checkedProperties,
        gridPage: 0
      };

    case SearchBoardActions.SEARCH_FAILURE:
      return {
        ...state,
        submitFormTrigger: false
      };

    case SearchBoardActions.CLEAR_SEARCH_RESULTS: {
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: [],
        },
        checkedProperties: [],
        searchResult: {
          totalRecords: 0,
          propertySummaryList: []
        },
        mySearchFieldsData: [],
        quickSearchFieldsData: [],
        selectedMySearch: state.sidePanelView.sidePanelTabOpened === SidePanelTab.mySearch ?
          clearSelectedMySearchValue(state.selectedMySearch) : (state.selectedMySearch),
        gridPage: 0,
        searchResultsCount: 0,
        currentPageName: 'Search Results'
      };
    }

    case SearchBoardActions.GET_GRID_EXPORT_INFO_SUCCESS:
      return {
        ...state,
        exportGridInfo: action.payload
      };

    case SearchBoardActions.GET_CUSTOM_EXPORT_INFO_SUCCESS:
      return {
        ...state,
        exportCustomGridInfo: action.payload
      };

    case SearchBoardActions.CREATE_CUSTOM_EXPORT_SUCCESS:
      return {
        ...state,
        exportCustomGridInfo: {
          ...state.exportCustomGridInfo,
          exportFormsList: [action.payload, ...state.exportCustomGridInfo.exportFormsList]
        }
      };

    case SearchBoardActions.UPDATE_CUSTOM_EXPORT_SUCCESS:
      return {
        ...state,
        exportCustomGridInfo: {
          ...state.exportCustomGridInfo,
          exportFormsList:
            updateExport(action.payload, [...state.exportCustomGridInfo.exportFormsList])
        }
      };

    case SearchBoardActions.DELETE_CUSTOM_EXPORT_SUCCESS:
      return {
        ...state,
        exportCustomGridInfo: {
          ...state.exportCustomGridInfo,
          exportFormsList:
            deleteExport(action.payload, [...state.exportCustomGridInfo.exportFormsList])
        }
      };

    case SearchBoardActions.GET_LOT_DIMENSIONS_SUCCESS:
      return {
        ...state,
        lotDimensions: action.payload
      };

    case SearchBoardActions.CHANGE_SHAPE:
      const payload = action.payload;
      const shapes = state.mapLayers.shapes.map((mapShape: Shape) => mapShape.id !== payload.id ? mapShape : ({
        ...mapShape,
        ...payload
      }));
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: shapes
        }
      };

    case SearchBoardActions.CHANGE_ANNOTATION_TOOL_STATUS:
      const status = !state.mapLayers.annotationToolStatus;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          annotationToolStatus: status
        }
      };

    case SearchBoardActions.ADD_ANNOTATION:
      const annotation = action.payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
         annotations: [...state.mapLayers.annotations, annotation]
        }
      };

    case SearchBoardActions.DELETE_ANNOTATION:
      const parcelId = action.payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          annotations: state.mapLayers.annotations.filter((el) => el.parcelId !== parcelId)
        }
      };

    case SearchBoardActions.CLEAR_SELECTED_LAYERS:
      return {
        ...state,
        boundaries: unselectLayers(state.boundaries),
        propertySales: {
          ...state.propertySales,
          distressedProperties: unselectLayers(state.propertySales.distressedProperties),
          locationInformation: unselectLayers(state.propertySales.locationInformation),
          mlsListings: unselectLayers(state.propertySales.mlsListings),
          salesValues: unselectLayers(state.propertySales.salesValues),
          selectedPropertyCharacteristic: PROPERTY_CHARACTERISTIC_NONE,
        },
        trends: {
          ...TRENDS_LAYER_DEFAULTS,
          availableLayers: state.trends.availableLayers
        },
        mapLayers: {
          ...state.mapLayers,
          selectedMapLayer: PREMIUM_MAP_LAYER_NONE,
          pointOfInterest: {
            ...state.mapLayers.pointOfInterest,
            selectedProperty: [],
            points: null
          },
          selectedDrawingTools:  null,
          shapes: [],
          lines: [],
          annotations: [],
          annotationToolStatus: false
        }
      };

    case SearchBoardActions.TOGGLE_BOUNDARY:
      return {
        ...state,
        boundaries: toggleLayer(state.boundaries, action.payload)
      };

    case SearchBoardActions.SET_BOUNDARY:
      const boundaryLayer = action.payload;
      return {
        ...state,
        boundaries: selectLayer(state.boundaries, boundaryLayer, boundaryLayer.isSelected)
      };

    case SearchBoardActions.TOGGLE_DISTRESSED_PROPERTY:
      return {
        ...state,
        propertySales: {
          ...state.propertySales,
          distressedProperties:
            toggleLayer(state.propertySales.distressedProperties, action.payload)
        }
      };

    case SearchBoardActions.TOGGLE_LOCATION_INFORMATION:
      return {
        ...state,
        propertySales: {
          ...state.propertySales,
          locationInformation:
            toggleLayer(state.propertySales.locationInformation, action.payload)
        }
      };

    case SearchBoardActions.TOGGLE_MLS_LISTING:
      return {
        ...state,
        propertySales: {
          ...state.propertySales,
          mlsListings: toggleLayer(state.propertySales.mlsListings, action.payload)
        }
      };

    case SearchBoardActions.TOGGLE_SALES_VALUE:
      return {
        ...state,
        propertySales: {
          ...state.propertySales,
          salesValues: toggleLayer(state.propertySales.salesValues, action.payload)
        }
      };

    case SearchBoardActions.SET_PROPERTY_CHARACTERISTIC:
      selectedPropertyCharacteristic = action.payload || PROPERTY_CHARACTERISTIC_NONE;
      return {
        ...state,
        propertySales: {
          ...state.propertySales,
          selectedPropertyCharacteristic
        }
      };

    case SearchBoardActions.SET_PREMIUM_LAYER:
      selectedPremiumMap = action.payload || PREMIUM_MAP_LAYER_NONE;
      let filterListItems;

      if ( action.lookups && action.payload.filterList) {
        filterListItems = action.payload.filterList.split(',');
        action.payload.filterListValues.forEach((codeValue) => {
          if (action.lookups.some((lookup: string) => {
            const newCodeValue = codeValue.code.replace(/\+/g, "").replace(/,/g, "").replace(/ /g, "").toLowerCase();
            const newLookup = lookup.toString().toLowerCase().replace(/\+/g, "").replace(/,/g, "").replace(/ /g, "");

            return newCodeValue.startsWith(newLookup);
          })) {
            filterListItems[codeValue.index - 1] = 1;
          }
        });

        selectedPremiumMap = {
          ...action.payload,
          filterList: filterListItems.join(',')
        };
      }

      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          selectedMapLayer: selectedPremiumMap
        }
      };

    case SearchBoardActions.SET_TRENDS_AGGREGATION_LEVEL:
      const aggregationLevel = action.payload;
      return {
        ...state,
        trends: {
          ...state.trends,
          aggregationLevel
        }
      };

    case SearchBoardActions.SET_TRENDS_PROPERTY_TYPE:
      const propertyType = action.payload;
      return {
        ...state,
        trends: {
          ...state.trends,
          propertyType
        }
      };

    case SearchBoardActions.SET_TRENDS_LAYER:
      const selectedTrendsLayer = action.payload;
      return {
        ...state,
        trends: {
          ...state.trends,
          selectedTrendsLayer
        }
      };

    case SearchBoardActions.SET_TRENDS_OPACITY:
      const opacity = action.payload;
      return {
        ...state,
        trends: {
          ...state.trends,
          opacity
        }
      };

    case SearchBoardActions.GET_MAP_LEGEND_DATA_SUCCESS:
      const mapLegendData = action.payload;
      return {
        ...state,
        mapLegendData
      };

    case SearchBoardActions.GET_TRENDS_LAYER_DATA:
      const trendsLayerRequest = action.payload;
      return {
        ...state,
        trendsLayerMarker: {
          ...state.trendsLayerMarker,
          isOpen: false,
          layerId: trendsLayerRequest.layerId,
          lat: trendsLayerRequest.lattitude,
          lng: trendsLayerRequest.longitude
        }
      };

    case SearchBoardActions.GET_TRENDS_LAYER_DATA_SUCCESS:
      const trendsLayerData = action.payload;
      return {
        ...state,
        trendsLayerMarker: {
          ...state.trendsLayerMarker,
          data: trendsLayerData,
          isOpen: true
        }
      };

    case SearchBoardActions.GET_FLOOD_ZONE_DATA:
      const floodZoneRequest = action.payload;
      return {
        ...state,
        floodZoneMarker: {
          ...state.floodZoneMarker,
          isOpen: false,
          layerId: floodZoneRequest.layerId,
          lat: floodZoneRequest.lattitude,
          lng: floodZoneRequest.longitude
        }
      };

    case SearchBoardActions.GET_FLOOD_ZONE_DATA_SUCCESS:
      const floodZoneData = action.payload;
      return {
        ...state,
        floodZoneMarker: {
          ...state.floodZoneMarker,
          data: floodZoneData,
          isOpen: true
        }
      };

    case SearchBoardActions.CHANGE_MAP_ZOOM:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          mapZoom: action.payload
        }
      };

    case SearchBoardActions.SET_MAP_MESSAGE:
      return {
        ...state,
        mapMessage: action.payload
      };

    case SearchBoardActions.APPLY_MAP_LAYERS_PREFERENCES: {
      const preferenceGroups = action.payload;
      const mapLayersPreferenceGroup = getPreferenceGroupByCode(preferenceGroups, PreferenceCode.PG_MAP_LAYERS);
      const trendsLayerGroup = getPreferenceGroupByCode(preferenceGroups, PreferenceCode.PG_MAP_TRENDS_LAYER);
      const toolsLayerGroup = getPreferenceGroupByCode(preferenceGroups, PreferenceCode.PG_MAP_TOOLS);
      const mlsListings = getMapLayers(
        mapLayersPreferenceGroup,
        MLS_LISTINGS_LAYERS
      );
      const salesValues = getMapLayers(
        mapLayersPreferenceGroup,
        SALES_VALUES_LAYERS
      );
      const propertyCharacteristics = getMapLayers(
        mapLayersPreferenceGroup,
        PROPERTY_CHARACTERISTICS_LAYERS
      );

      let premiumLayers = getMapLayers(
        toolsLayerGroup,
        PREMIUM_SEARCH_LAYERS
      );

      if (action.premiumSearchMinimumZoom) {
        premiumLayers = premiumLayers.map((layer) => {
          return { ...layer, minZoom: action.premiumSearchMinimumZoom };
        });
      }

      selectedPremiumMap = premiumLayers.find((layer: LayerInfo) => layer.isSelected) || PREMIUM_MAP_LAYER_NONE;

      selectedPropertyCharacteristic = propertyCharacteristics.find((layer: LayerInfo) => layer.isSelected) || PROPERTY_CHARACTERISTIC_NONE;

      const distressedProperties = getMapLayers(
        getPreferenceGroupByCode(preferenceGroups, PreferenceCode.PG_MAP_DISTRESSED_PROPERTIES),
        DISTRESSED_PROPERTY_LAYERS
      );
      const locationInformation = getMapLayers(
        getPreferenceGroupByCode(preferenceGroups, PreferenceCode.PG_MAP_LAYERS_LOCATION_INFO),
        LOCATION_INFORMATION_LAYERS
      );
      const boundaries = getMapLayers(
        getPreferenceGroupByCode(preferenceGroups, PreferenceCode.PG_MAP_BOUNDARIES),
        BOUNDARY_LAYERS
      );
      const trendsLayers = getMapLayers(
        trendsLayerGroup,
        TREND_LAYERS
      );


      const selectedLayerId = PreferenceUtils.getPreferenceValue(
        PreferenceUtils.getPreferenceElement(trendsLayerGroup, PreferenceCode.PREF_MAP_TENDS_LAYER),
      );
      const selectedLayer = TREND_LAYERS.find((layer: LayerInfo) => layer.layerId === selectedLayerId);


      const selectedAggregationLevel = PreferenceUtils.getPreferenceValue(
        PreferenceUtils.getPreferenceElement(trendsLayerGroup, PreferenceCode.PREF_MAP_AGGREGATION)
      );

      const selectedPropertyType = PreferenceUtils.getPreferenceValue(
        PreferenceUtils.getPreferenceElement(trendsLayerGroup, PreferenceCode.PREF_MAP_TRENDS_PROPERTY_TYPE)
      );

      const selectedOpacity = PreferenceUtils.getPreferenceValue(
        PreferenceUtils.getPreferenceElement(trendsLayerGroup, PreferenceCode.PREF_MAP_TRENDS_OPACITY)
      );

      const selectedPointsOfInterest = (PreferenceUtils.getPreferenceValue(PreferenceUtils.getPreferenceElement(
        PreferenceUtils.getPreferenceGroup(preferenceGroups, PreferenceCode.PG_MAP_TOOLS), PreferenceCode.PREF_MAP_POINTS_OF_INTEREST
      )) || '').split(' ').filter(Boolean).map((pointKey) => {
        return Object.keys(PointOfInterestPreferenceValues)
          .find(((value) => PointOfInterestPreferenceValues[value] === pointKey)) as PointOfInterestProperty;
      });

      return {
        ...state,
        boundaries,
        propertySales: {
          ...state.propertySales,
          mlsListings,
          salesValues,
          distressedProperties,
          propertyCharacteristics,
          locationInformation,
          selectedPropertyCharacteristic
        },
        trends: {
          ...state.trends,
          availableLayers: trendsLayers,
          selectedTrendsLayer: selectedLayer || TRENDS_TYPE_NONE,
          aggregationLevel: selectedAggregationLevel as TrendsAggregationLevel,
          propertyType: selectedPropertyType as TrendsPropertyType,
          opacity: parseInt(selectedOpacity, 10)
        },
        mapLayers: {
          ...state.mapLayers,
          selectedMapLayer: selectedPremiumMap,
          availableLayers: premiumLayers,
          pointOfInterest: {
            ...state.mapLayers.pointOfInterest,
            selectedProperty: selectedPointsOfInterest
          }
        }
      };
    }

    case UserActions.REMOVE_CUSTOM_MY_SEARCH_SUCCESS:
      const removeData = action.payload;
      return {
        ...state,
        selectedMySearch: state.selectedMySearch.templateCode === removeData.removedTemplateCode ?
          removeData.defaultTemplate : state.selectedMySearch,
        selectedMySearchModal: removeData.defaultTemplate
      };

    case SearchBoardActions.APPLY_EDIT_MY_SEARCH:
    case SearchBoardActions.SET_SELECTED_MY_SEARCH:
      const selectedSearch = (action as SearchBoardActions.SetSelectedMySearch).payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: createShapes(selectedSearch)
        },
        selectedMySearch: selectedSearch,
        selectedMySearchModal: selectedSearch,
        mySearchFieldsData: []
      };

    case UserActions.UPDATE_CUSTOM_MY_SEARCH_SUCCESS:
      const updatedTemplate: Template = action.payload;
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: createShapes(updatedTemplate)
        },
        selectedMySearch: state.selectedMySearch.templateCode === updatedTemplate.templateCode ?
          ({...updatedTemplate, modifiedDate: new Date().toJSON()}) : state.selectedMySearch,
        selectedMySearchModal: state.selectedMySearchModal.templateCode === updatedTemplate.templateCode ?
          ({...updatedTemplate, modifiedDate: new Date().toJSON()}) : state.selectedMySearchModal
      };

    case SearchBoardActions.SET_SELECTED_MY_SEARCH_MODAL:
      return {
        ...state,
        selectedMySearchModal: action.payload
      };

    case UserActions.SAVE_CUSTOM_MY_SEARCH_SUCCESS:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          shapes: createShapes(action.payload)
        },
        selectedMySearch: action.payload,
        mySearchFieldsData: []
      };

    case SearchBoardActions.SAVE_SELECTED_EDIT_SEARCH_FROM_MODAL_SUCCESS:
      return {
        ...state,
        selectedMySearchModal: {...action.payload, modifiedDate: new Date().toJSON()}
      };

    case SearchBoardActions.SET_GRID_PAGE:
      return {
        ...state,
        gridPage: action.payload
      };

    case SearchBoardActions.GET_PROPERTY_INFORMATION_SUCCESS:
      return {
        ...state,
        propertyInformation: action.payload
      };

    case SearchBoardActions.SET_DRIVE_DIRECTION:
      return {
        ...state,
        driveDirection: action.payload
      };

    case SearchBoardActions.SET_DRIVE_DIRECTION_RESULT:
      return {
        ...state,
        driveDirectionResult: action.payload
      };

    case SearchBoardActions.LOAD_MAP_POINT_OF_INTEREST_CONFIG_SUCCESS:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          pointOfInterest: {
            ...state.mapLayers.pointOfInterest,
            config: action.payload
          }
        }
      };

    case SearchBoardActions.GET_POINT_OF_INTEREST_POINTS_SUCCESS:
      if (!state.mapLayers.pointOfInterest.selectedProperty.length) {
        return state;
      }
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          pointOfInterest: {
            ...state.mapLayers.pointOfInterest,
            points: action.payload
          }
        }
      };

    case SearchBoardActions.ADD_POINT_OF_INTEREST_PROPERTY:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          pointOfInterest: {
            ...state.mapLayers.pointOfInterest,
            selectedProperty: [...state.mapLayers.pointOfInterest.selectedProperty, action.payload]
          }
        }
      };

    case SearchBoardActions.REMOVE_POINT_OF_INTEREST_PROPERTY:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          pointOfInterest: {
            ...state.mapLayers.pointOfInterest,
            selectedProperty: [
              ...state.mapLayers.pointOfInterest.selectedProperty
                .filter((prop: PointOfInterestProperty) => prop !== action.payload)
            ],
            points: (removeObjectProperty(state.mapLayers.pointOfInterest.points, action.payload) as PointOfInterestPoints) || null
          }
        }
      };

    case SearchBoardActions.GET_LOOK_UPS_FOR_NEW_REGIONS:
      return {
        ...state,
        lookups: null
      };

    case SearchBoardActions.GET_LOOK_UPS_SUCCESS:
      const lookups = action.payload;
      return {
        ...state,
        lookups: {
          ...state.lookups,
          ...lookups
        }
      };

    case SearchBoardActions.GET_LOOK_UPS_FOR_SEARCH_MULTI_FIELD_SUCCESS:
      return {
        ...state,
        searchMultiFieldLookUps: action.payload
      };

    case SearchBoardActions.SUBMIT_SEARCH:
      return {
        ...state,
        submitFormTrigger: true
      };

    case SearchBoardActions.GET_REGION_GEOCODES_SUCCESS:
      return {
        ...state,
        mapCoordinates: action.payload
      };

    case SearchBoardActions.SET_LOCATE_ON_MAP_TOOL:
      return {
        ...state,
        mapLayers: {
          ...state.mapLayers,
          locateOnMapStatus: action.payload
        }
      };

    case SearchBoardActions.GET_SEARCH_RESULTS_COUNT:
      return {
        ...state,
        searchResultsCount: NaN
      };

    case SearchBoardActions.GET_SEARCH_RESULTS_COUNT_SUCCESS:
      return {
        ...state,
        searchResultsCount: action.payload
      };

    case SearchBoardActions.SET_CURRENT_PAGE_NAME:
      return {
        ...state,
        currentPageName: action.payload
      };

    case SearchBoardActions.CLEAR_SEARCH_RESULTS_COUNT:
    case SearchBoardActions.GET_SEARCH_RESULTS_COUNT_FAILURE:
      return {
        ...state,
        searchResultsCount: 0
      };

    case SearchBoardActions.UPDATE_SEARCH_RESULT_SORTING_PREFERENCE:
      return {
        ...state,
        searchResultsSortingPreference: action.payload
      };

    case UserActions.LOGIN_SUCCESS:
      const preferenceGroupList = action.payload.prefAndTempData.preferenceGroupList;
      const mapCoordinates = PreferenceUtils.getPreferenceElementValuesFromGroup(preferenceGroupList,
        PreferenceCode.PG_RL_GENERAL_PREFERENCES,
        PreferenceCode.PG_GENPREF_MAP_BOUNDS)
        .map((coordinate: string) => JSON.parse(coordinate));


      return {
        ...state,
        mapCoordinates,
        gridViewState: getGridViewState(preferenceGroupList)
      };

    case UserActions.SAVE_REGIONS_SUCCESS:
      return {
        ...state,
        mapCoordinates: action.payload.mapBounds
      };

    default: {
      return state;
    }
  }
}

function getPreferenceGroupByCode(preferenceGroups: Array<PreferenceGroup>, code: PreferenceCode): PreferenceGroup {
  return preferenceGroups.find((prefGroup) => prefGroup.prefGroupCode === code);
}

function getMapLayers(preferenceGroup: PreferenceGroup, layers: Array<LayerInfo>): Array<LayerInfo> {
  const layersPreferences = (preferenceGroup && preferenceGroup.preferenceElements || [])
    .filter((preference: PreferenceElement) => preference.hasAccess);

  return layers
    .map((layer: LayerInfo) => {
      const preference: PreferenceElement = layersPreferences.find((layersPreference) => {
        return layer.preferenceElementCode === layersPreference.prefCode;
      });
      if (preference) {
        const isSelected = PreferenceUtils.hasPreferenceValue(preference, 'true')
          || layer.layerId === LayerId.PARCEL_LAYER && PreferenceUtils.hasPreferenceValue(preference, DisplayOptions.YES)
          || PreferenceUtils.hasPreferenceValue(preference, layer.layerId);

        const minZoom = preference.minZoom
          ? preference.minZoom
          : preference.additionalInfoParsed && preference.additionalInfoParsed.minZoom
            ? Number(preference.additionalInfoParsed.minZoom)
            : null;

        const maxZoom = preference.maxZoom
          ? preference.maxZoom
          : preference.additionalInfoParsed && preference.additionalInfoParsed.maxZoom
            ? Number(preference.additionalInfoParsed.maxZoom)
            : null;

        return {minZoom, maxZoom, isSelected, ...layer};
      }
      return null;
    })
    .filter(Boolean);
}

function selectRow(state: SearchBoardState, action: SearchBoardActions.SelectRow): SelectedRow {
  const {selection, rowId} = action.payload;

  if (selection === (rowId === state.selectedRowId.current)) {
    return state.selectedRowId;
  } else {
    if (selection) {
      return {current: rowId, previous: state.selectedRowId.current};
    } else {
      return {current: '', previous: state.selectedRowId.current};
    }
  }
}

function getNewCheckedProperties(checkedProperties: Array<string>, id: string, isChecked: boolean): Array<string> {
  if (isChecked) {
    return [...checkedProperties, id];
  }
  return checkedProperties.filter((propertyId: string) => propertyId !== id);
}

function deleteExport(id: string, list: Array<ExportTemplate>): Array<ExportTemplate> {
  list.splice(findExportIndex(id, list), 1);
  return list;
}

function updateExport(exp: ExportTemplate, list: Array<ExportTemplate>): Array<ExportTemplate> {
  const index = findExportIndex(exp.template.templateCode, list);
  list[index] = exp;
  return list;
}

function findExportIndex(id: string, list: Array<ExportTemplate>): number {
  return list.findIndex((item: ExportTemplate) => item.template.templateCode === id);
}

function toggleLayer(layerList: Array<LayerInfo>, layer: LayerInfo): Array<LayerInfo> {
  return layerList.map((layerInfo) => {
    if (layerInfo.layerId === layer.layerId) {
      let subLayers = layerInfo.subLayers;
      if (subLayers) {
        subLayers = layerInfo.isSelected
          ? unselectLayers(layerInfo.subLayers)
          : selectLayer(layerInfo.subLayers, layerInfo.subLayers[0]);
      }
      return {...layerInfo, isSelected: !layerInfo.isSelected, subLayers};
    }

    if (layerInfo.isSelected && layerInfo.subLayers) {
      return {
        ...layerInfo,
        subLayers: selectLayer(layerInfo.subLayers, layer)
      };
    }
    return layerInfo;
  });
}

function selectLayer(layerList: Array<LayerInfo>, layer: LayerInfo, isSelected: boolean = true): Array<LayerInfo> {
  const includedInList = layerList.some((layerInfo) => layerInfo.layerId === layer.layerId);
  if (includedInList) {
    return layerList.map((layerInfo) => {
      if (layerInfo.layerId === layer.layerId) {
        return {...layerInfo, isSelected};
      } else {
        return {...layerInfo, isSelected: false};
      }
    });
  }
  return layerList;
}

function unselectLayers(layerList: Array<LayerInfo>): Array<LayerInfo> {
  return layerList.map((layerInfo) => {
    const unselectedLayer = {...layerInfo, isSelected: false};
    if (layerInfo.subLayers) {
      return {...unselectedLayer, subLayers: unselectLayers(layerInfo.subLayers)};
    }
    return unselectedLayer;
  });
}

function createShapes(template: Template): Array<Shape> {
  if (!template) {
    return [];
  }
  const mapSearch = template.dataElementList.find((el: DataElement) => el.fieldCode === FieldCode.MAP);
  if (!mapSearch || !mapSearch.customAttributeInfo || !mapSearch.customAttributeInfo.customAttrib2) {
    return [];
  }
  const shapes: Array<Shape> = JSON.parse(mapSearch.customAttributeInfo.customAttrib2) || [];
  if (!shapes.length) {
    return [];
  }

  shapes.forEach((shape: Shape, index: number) => {
    if (shape.type === OverlayType.CIRCLE || (shape.type === OverlayType.RECTANGLE && shape.bounds)) {
      return;
    }

    if (mapSearch.operatorValueList[index]) {
      const shapePath = mapSearch.operatorValueList[index].values;

      shape.latLong = shapePath.map((el: string) => {
        const [lng, lat] = el.split(', ');
        return {lat: Number(lat), lng: Number(lng)};
      });
    }
  });

  return shapes;
}

function removeObjectProperty(obj: object, property: string) {
  if (obj === null) {
    return null;
  }

  const objCopy = Object.assign({}, obj);
  if (obj.hasOwnProperty(property)) {
    delete objCopy[property];
  }

  return objCopy;
}

function clearSelectedMySearchValue(template: Template): Template {
  const templateCopy = {...template};
  templateCopy.dataElementList = template.dataElementList.reduce((prev: Array<DataElement>, curr: DataElement) => {
    if (curr.fieldCode === FieldCode.MAP) {
      return prev;
    }
    const defaultOperator = curr.operatorValueList.find((operatorValue) => operatorValue.operator === curr.defaultOperator) || curr.operatorValueList[0];

    prev.push({...curr, operatorValueList: [{...defaultOperator, values: ['']}]});
    return prev;
  }, []);

  return templateCopy;
}

function getGridViewState(preferenceGroupList: Array<PreferenceGroup>): GridViewState {
  const gridStatePreferenceValue = PreferenceUtils.getPreferenceElementValuesFromGroup(preferenceGroupList,
    PreferenceCode.PG_MAP_CONFIG,
    PreferenceCode.PREF_MAP_MAP_TO_GRID_RATIO);
  const mapHeight: number = gridStatePreferenceValue.length > 0 ? Number(gridStatePreferenceValue[0]) : NaN;
  if (isNaN(mapHeight)) {
    return {
      map: GridItemSize.HALF,
      list: GridItemSize.HALF
    };
  }
  return {
    map: mapHeight,
    list: 100 - mapHeight
  };
}
